import React, { Fragment, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import Button, { ButtonGroup } from 'components/Button';
import Flex from 'components/Flex';
import { SearchInput } from 'components/Form/DEPRECATED/FormElements';
import { SearchInputProps } from 'components/Form/DEPRECATED/FormElements/SearchInput/types';
import Select, { BaseOption } from 'components/Form/Inputs/Select/Select';
import { FilterIcon } from 'components/Icons';
import { ContainerPaddingCss, InverseContainerCss } from 'components/Layout/Container';
import { TableSortDirection } from 'components/Lists/TableTypes';
import { COLORS } from 'config/constants';
import { FilterSlug } from 'routes/Label/LabelSubscribers/LabelSubscriberOptions';

/**
 * [TableFilterControls] - Table parent header for contolling search, filters, etc.
 *
 * Desktop Large Layout:
 * | {                    renderCustomHeader()                          }|
 * | [search] [filter1] [filter2]   --------   [action1] [action2] [•••] |  <- scroll on x overflow ->
 *
 * Mobile Layout:
 * | {          renderCustomHeader()          }|
 * | [           search             ] [action] |
 * | [filter1] [filter2] [filter...] | [ sort ]|  <- scroll on x overflow ->
 */

export type SortByConfig<Sort extends string = string> = Record<Sort, TableSortDirection>;

export interface FilterSortOption extends BaseOption {
  id: string;
  direction: TableSortDirection;
  sortBy?: SortByConfig;
  value: string;
}

type Filter = { cont: string | null } | { is: boolean } | { eq: string | null };
export type FilterByConfig = { [key: string]: Filter | undefined };

export interface FilterByOption extends BaseOption {
  filterBy: FilterByConfig;
  value: FilterSlug;
}

interface BaseFilterProps {
  id?: string;
  name: string;
  active?: boolean;
  isSearchable?: boolean;
  style?: React.CSSProperties;
  testId?: string;
  disabled?: boolean;
}

interface FilterByProps extends BaseFilterProps {
  options: FilterByOption[];
  onChange: (option: FilterByOption) => void;
  value: FilterByOption;
}

// Typically used to show sorting options on mobile lists:
export interface FilterSortProps extends BaseFilterProps {
  sort: boolean;
  options: FilterSortOption[];
  onChange: (option: FilterSortOption) => void;
  value: FilterSortOption;
}

export type FilterProps = FilterByProps | FilterSortProps;

interface ActionProps {
  name: string;
  label: string | React.ReactNode;
  onClick: () => void;
  'data-testid': string;
}

interface Props {
  // [withinContainer] if true, the component will offset any wrapping container padding
  withinContainer?: boolean;
  // [renderCustomHeader] renders header above the search input. Useful for warning messages / call to action
  renderCustomHeader?: () => React.ReactNode;
  searchInput: SearchInputProps;
  // [filters] config array used to control table filtering and/or sorting
  filters?: FilterProps[];
  // [actions] Special actions related to the table (i.e. "+ Add thing")
  actions?: ActionProps[];
  // [clearFilters] function that will be run to unset any applied filters with clear button
  clearFilters?: () => void;
  // [mobile] if true, the mobile layout will be applied and the header will be sticky on scroll
  mobile?: boolean;
}

type ContainerProps = PropsWithChildren<{
  $withinContainer?: boolean;
  $desktop?: boolean;
}>;

const Container = styled.div<ContainerProps>`
  ${({ $withinContainer }) => $withinContainer && InverseContainerCss}
  ${({ $withinContainer }) => $withinContainer && `width: initial !important;`}
  ${({ $withinContainer, $desktop }) =>
    $withinContainer && $desktop && ContainerPaddingCss}
  .SearchInput {
    margin-bottom: 0;
    min-width: 24rem;
    input {
      height: 100%;
    }
  }
`;

const MobileContainer = styled(Container)<ContainerProps>`
  background: ${COLORS.GRAY_LIGHTEST};
  border-bottom: 1px solid ${COLORS.GRAY_LIGHT};
  overflow: hidden;
  position: sticky;
  top: 44px;
  z-index: 1;
`;

const DesktopContainer = styled(Container)<ContainerProps>`
  overflow-x: auto;
  padding-top: 15px;
  padding-bottom: 20px;
  display: flex;
  justify-content: space-between;
  .SearchInput {
    padding-right: 10px;
    max-width: 44rem;
  }
`;

const MobileSearchRow = styled.div<{ $hasFilters?: boolean; $withinContainer?: boolean }>`
  display: flex;
  padding: 10px 0 ${({ $hasFilters }) => ($hasFilters ? 0 : 10)}px;
  ${({ $withinContainer }) => $withinContainer && ContainerPaddingCss}
`;

const MobileFilterRow = styled.div<{ $withinContainer?: boolean }>`
  display: flex;
  overflow-x: auto;
  width: 100%;
  ${({ $withinContainer }) => $withinContainer && ContainerPaddingCss}
  padding-top: 8px;
  padding-bottom: 10px;
`;

export const Filter = styled.div`
  position: relative;
  margin-right: 9px;
`;

export const FilterCount = styled.div`
  align-items: center;
  background: ${COLORS.PRIMARY};
  border-radius: 11px;
  bottom: 0px;
  color: ${COLORS.WHITE};
  display: flex;
  font-size: 8px;
  height: 11px;
  justify-content: center;
  padding-left: 1px;
  position: absolute;
  right: -5px;
  width: 11px;
`;

const FilterDivider = styled.div`
  margin: 5px;
  &:after {
    content: '';
    display: block;
    height: 100%;
    width: 1px;
    background: ${COLORS.GRAY_LIGHT};
  }
`;

const StyledButtonGroup = styled(ButtonGroup)`
  margin: 0;
`;

// when scrolling, there isn't any padding preserved to the right of the last filter button
const ScrollSpacer = styled.div`
  margin: 0;
  &:after {
    content: '';
    display: block;
    height: 100%;
    width: 5px;
  }
`;

interface FilterGroupPropTypes {
  filters: FilterProps[];
  clearFilters?: () => void;
}

const FilterGroup = ({ filters, clearFilters }: FilterGroupPropTypes) => {
  if (filters.length === 0) return null;
  const activeFilters = filters.filter(({ active }) => active);

  return (
    <StyledButtonGroup>
      {activeFilters.length > 0 && clearFilters && (
        <Button
          outline
          rounded
          onClick={clearFilters}
          color={Button.COLORS.NEUTRAL}
          style={{ minWidth: 85, background: COLORS.WHITE }}
        >
          <Filter>
            <FilterIcon />
            <FilterCount>{activeFilters.length}</FilterCount>
          </Filter>
          Clear
        </Button>
      )}

      {filters.map(filter => {
        const { name, value, testId, isSearchable = false, ...props } = filter;

        return (
          <Fragment key={name}>
            {'sort' in filter && filters.length > 1 && <FilterDivider />}
            <Select
              name={name}
              data-testid={testId}
              // We prefix the selected value label with a "Sort: " label to make the dropdown more clear
              value={
                'sort' in filter
                  ? {
                      ...value,
                      label: (
                        <span>
                          <strong>Sort: </strong>
                          {value.label}
                        </span>
                      )
                    }
                  : value
              }
              customTheme="button"
              isSearchable={isSearchable}
              menuPortalTarget={document.getElementById('portals-root')}
              // TODO: we should just use 'disabled' attribute in Select to be consistent with html input
              disabled={props.disabled}
              {...props}
            />
          </Fragment>
        );
      })}

      <ScrollSpacer />
    </StyledButtonGroup>
  );
};

const ActionGroup = ({
  actions,
  style
}: {
  actions: { label: ReactNode; onClick: () => void }[];
  style?: React.CSSProperties;
}) => {
  if (actions.length === 0) return null;

  return (
    <StyledButtonGroup style={style}>
      {actions.map(({ label, onClick, ...rest }) => (
        <Button key={String(label)} onClick={onClick} rounded {...rest}>
          {label}
        </Button>
      ))}
    </StyledButtonGroup>
  );
};

export const TableFilterControls = ({
  withinContainer,
  renderCustomHeader,
  searchInput,
  filters = [],
  actions = [],
  clearFilters,
  mobile // mobile sticky header
}: Props) => {
  if (mobile) {
    return (
      <MobileContainer $withinContainer={withinContainer}>
        {renderCustomHeader && renderCustomHeader()}

        <MobileSearchRow
          $hasFilters={!!filters.length}
          $withinContainer={withinContainer}
        >
          <SearchInput {...searchInput} />

          <ActionGroup actions={actions} style={{ marginLeft: 10 }} />
        </MobileSearchRow>

        {filters.length > 0 && (
          <MobileFilterRow $withinContainer={withinContainer}>
            <FilterGroup filters={filters} clearFilters={clearFilters} />
          </MobileFilterRow>
        )}
      </MobileContainer>
    );
  }

  return (
    <div>
      {renderCustomHeader && renderCustomHeader()}
      <DesktopContainer $withinContainer={withinContainer} $desktop>
        <Flex>
          <SearchInput {...searchInput} />

          <FilterGroup filters={filters} clearFilters={clearFilters} />
        </Flex>

        <ActionGroup actions={actions} />
      </DesktopContainer>
    </div>
  );
};

export default TableFilterControls;
